import "./Home.css";
import { useState, useEffect, useContext, useMemo } from "react";
import { Button, Card, Spin, Empty, Tag, Row, Col } from "antd";
import { AppContext } from "../../../context/AppContextProvider";
import { getHomeDataSummary } from "../../../network/network";
import moment from "moment";
import { useTranslation } from "react-i18next";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { AddonName, TransactionType } from "../../../configs/constants";
import { useNavigate } from "react-router-dom";
import TrialAddonsModal from "./TrialAddonsModal";
import { handleApiError } from "../../../utils/errorHandler";

const CHART_COLORS = ["#4285f4", "#63c7c7", "#9aa6b2"];

const DEFAULT_DASHBOARD_DATA = {
  totals: { amount: 0, tax: 0, refunds: 0 },
  quarterly_transactions: [],
  transaction_by_types: [],
  last_15_days_sale: [],
  branch_selling_products: [],
  inventry_status: [],
  low_stock_products: [],
  total_orders: 0,
  waiting_for_approval: 0,
};

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  hero: {
    height: "100%",
    minHeight: 165,
    borderRadius: 18,
    padding: 24,
    overflow: "hidden",
    color: "#ffffff",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
    background:
      "linear-gradient(135deg, #101642 0%, #1c274d 52%, #314055 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    position: "relative",
  },
  heroEyebrow: {
    marginBottom: 8,
    color: "#bfe11b",
    fontSize: 13,
    fontWeight: 800,
  },
  heroTitle: {
    margin: 0,
    color: "#ffffff",
    fontSize: "clamp(24px, 2vw, 36px)",
    fontWeight: 800,
    lineHeight: 1.2,
  },
  heroMark: {
    width: 88,
    height: 88,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgb(241 246 201 / 40%)",
    fontSize: 42,
    flex: "0 0 auto",
  },
  heroGlow: {
    position: "absolute",
    zIndex: 0,
    top: -38,
    insetInlineEnd: -42,
    width: 190,
    height: 190,
    borderRadius: "50%",
    background: "rgba(191, 225, 27, 0.14)",
    pointerEvents: "none",
  },
  heroContent: {
    minWidth: 0,
    position: "relative",
    zIndex: 2,
    flex: 1,
  },
  heroMeta: {
    marginTop: 14,
  },
  heroBranchPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 30,
    padding: "6px 12px",
    borderRadius: 999,
    background: "rgba(255, 255, 255, 0.10)",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    color: "rgba(255, 255, 255, 0.84)",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
  },
  heroBranchDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#bfe11b",
    boxShadow: "0 0 0 4px rgba(191, 225, 27, 0.14)",
    flex: "0 0 auto",
  },
  card: {
    height: "100%",
    overflow: "hidden",
  },
  cardHead: {
    minHeight: 56,
    padding: "0 20px",
    borderBottom: "1px solid var(--color-border)",
    color: "var(--color-heading)",
    fontSize: 18,
    fontWeight: 700,
  },
  statCard: {
    height: "100%",
    minHeight: 118,
    overflow: "hidden",
    position: "relative",
  },
  statCircle: {
    position: "absolute",
    top: -34,
    insetInlineEnd: -26,
    width: 86,
    height: 86,
    borderRadius: "50%",
    background: "var(--color-primary-shadow, rgba(191, 225, 27, 0.18))",
    pointerEvents: "none",
  },
  statContent: {
    position: "relative",
    zIndex: 1,
  },
  statLabel: {
    color: "var(--color-text)",
    fontSize: 13,
    fontWeight: 700,
  },
  statValue: {
    marginTop: 8,
    color: "var(--color-heading)",
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  statHint: {
    marginTop: 8,
    color: "var(--color-success-hover)",
    fontSize: 13,
    fontWeight: 700,
  },
  softRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    padding: "13px 14px",
    borderRadius: 14,
    background: "var(--color-light-bg, var(--color-bg))",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
};

const normalizeChartSource = (source) => {
  if (Array.isArray(source)) return source;

  if (source && typeof source === "object") {
    return Object.entries(source).map(([key, value]) =>
      value && typeof value === "object"
        ? { label: key, ...value }
        : { label: key, value },
    );
  }

  return [];
};

const getNumberFromKeys = (item, keys) => {
  for (const key of keys) {
    const value = item?.[key];

    if (value !== undefined && value !== null && value !== "") {
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? 0 : numberValue;
    }
  }

  return 0;
};

const getQuarterLabel = (item, index) => {
  const monthValue =
    item?.month_year ||
    item?.month_name ||
    item?.month ||
    item?.period ||
    item?.date ||
    item?.start_date ||
    item?.created_at ||
    item?.name ||
    item?.title ||
    item?.label ||
    `Item ${index + 1}`;

  if (item?.year && item?.month && /^\d+$/.test(String(item.month))) {
    return moment(
      `${item.year}-${String(item.month).padStart(2, "0")}`,
      "YYYY-MM",
    ).format("MMMM, YYYY");
  }

  const parsedDate = moment(
    monthValue,
    [
      "YYYY-MM",
      "YYYY-MM-DD",
      "MMMM, YYYY",
      "MMM, YYYY",
      "MMMM YYYY",
      "MMM YYYY",
    ],
    true,
  );

  return parsedDate.isValid() ? parsedDate.format("MMMM, YYYY") : monthValue;
};

const buildQuarterlyChartData = (source, valueKeys) =>
  normalizeChartSource(source)
    .slice(-3)
    .map((item, index) => ({
      label: getQuarterLabel(item, index),
      value: getNumberFromKeys(item, valueKeys),
    }));

const Home = () => {
  const { authUser, appConfig, displayWebView, roundOff, refreshAuthUser } =
    useContext(AppContext);

  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(DEFAULT_DASHBOARD_DATA);
  const [trialModalVisible, setTrialModalVisible] = useState(false);
  const [expiringAddons, setExpiringAddons] = useState([]);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const is_order_management_active = useMemo(
    () =>
      authUser.active_addons?.some(
        (addon) => addon?.id && addon?.name === AddonName.OrderManagement,
      ),
    [authUser.active_addons],
  );

  const is_stock_management_active = useMemo(
    () =>
      authUser.active_addons?.some(
        (addon) => addon?.id && addon?.name === AddonName.Stock,
      ),
    [authUser.active_addons],
  );

  useEffect(() => {
    refreshAuthUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isEffectActive = true;
    const startDate = moment().subtract(14, "d");
    const endDate = moment();

    setIsLoading(true);

    getHomeDataSummary()
      .then((response) => {
        if (response?.data?.success === false) {
          if (isEffectActive) handleApiError(response, t);
          return;
        }

        const data = response?.data?.data || {};
        const rawSales = data.last_15_days_sale || {};
        const last15DaysSale = [];
        const currentDate = startDate.clone();

        while (endDate.diff(currentDate, "days") >= 0) {
          const key = currentDate.format("YYYY-MM-DD");

          last15DaysSale.push({
            date: currentDate.format("Do MMM"),
            sales: Number(rawSales?.[key]?.total_sales || 0),
          });

          currentDate.add(1, "d");
        }

        if (isEffectActive) {
          setDashboardData({
            ...DEFAULT_DASHBOARD_DATA,
            ...data,
            totals: {
              ...DEFAULT_DASHBOARD_DATA.totals,
              ...(data.totals || {}),
            },
            quarterly_transactions: data.quarterly_transactions || [],
            transaction_by_types: data.transaction_by_types || [],
            last_15_days_sale: last15DaysSale,
            branch_selling_products: data.branch_selling_products || [],
            inventry_status: data.inventry_status || [],
            low_stock_products: data.low_stock_products || [],
            total_orders: data.total_orders || 0,
            waiting_for_approval: data.waiting_for_approval || 0,
          });
        }
      })
      .catch((error) => {
        if (isEffectActive) handleApiError(error, t);
      })
      .finally(() => {
        if (isEffectActive) setIsLoading(false);
      });

    return () => {
      isEffectActive = false;
    };
  }, [t]);

  useEffect(() => {
    if (authUser.is_onboarding_complete === 1 && displayWebView === "webview") {
      window.location.href = "https://anyposmobileapp.page.link/76UZ";
    }
  }, [authUser, displayWebView]);

  useEffect(() => {
    if (!Array.isArray(authUser?.active_addons)) return;

    const today = moment().startOf("day");

    const todayExpiring = authUser.active_addons.filter((addon) => {
      if (!addon?.trial_ended_at) return false;

      const price = parseFloat(addon?.price ?? 0);
      const isPaidAddon = !Number.isNaN(price) && price > 0;

      return (
        addon?.is_wanted === false &&
        isPaidAddon &&
        moment(addon.trial_ended_at).startOf("day").isSame(today, "day")
      );
    });

    setExpiringAddons(todayExpiring);
    setTrialModalVisible(todayExpiring.length > 0);
  }, [authUser?.active_addons]);

  const categoryItems = useMemo(() => {
    const list = (authUser.categories || []).map((cat) => ({
      name: appConfig.direction === "rtl" ? cat.name_ar : cat.name,
      count: cat.products?.length || 0,
    }));

    const total = list.reduce((sum, item) => sum + item.count, 0) || 1;

    return list
      .filter((item) => item.count > 0)
      .map((item) => ({
        ...item,
        percent: Math.round((item.count / total) * 100),
      }));
  }, [authUser.categories, appConfig.direction]);

  const quarterlySalesData = useMemo(
    () =>
      buildQuarterlyChartData(dashboardData.quarterly_transactions, [
        "total_sales",
        "sales",
        "amount",
        "total_amount",
        "net_sales",
        "value",
      ]),
    [dashboardData.quarterly_transactions],
  );

  const quarterlyTaxData = useMemo(
    () =>
      buildQuarterlyChartData(dashboardData.quarterly_transactions, [
        "taxes",
        "total_tax",
        "tax",
        "tax_amount",
        "total_taxes",
        "value",
      ]),
    [dashboardData.quarterly_transactions],
  );

  const paymentTypesData = useMemo(
    () =>
      [
        {
          label: t("cash"),
          value: Number(
            dashboardData.transaction_by_types?.[TransactionType.Cash]?.count ||
              0,
          ),
        },
        {
          label: t("credit_card"),
          value: Number(
            dashboardData.transaction_by_types?.[TransactionType.Credit]
              ?.count || 0,
          ),
        },
        {
          label: t("multi_payment"),
          value: Number(
            dashboardData.transaction_by_types?.[TransactionType.Multipayment]
              ?.count || 0,
          ),
        },
      ].filter((item) => item.value > 0),
    [dashboardData.transaction_by_types, t],
  );

  const renderMoneyValue = (value) =>
    value ? <CurrencyFormatter value={roundOff(value)} /> : "0";

  const getProductName = (product) =>
    (appConfig.direction === "rtl" ? product.name : product.name_en) ||
    product.name_en ||
    product.name ||
    "-";

  const formatDonutValue = (value) => {
    const roundedValue = roundOff(Number(value) || 0);
    const numberValue = Number(roundedValue);

    return Number.isInteger(numberValue) ? numberValue : roundedValue;
  };

  const renderDashboardCard = ({ title, extra, children, bodyStyle }) => (
    <Card
      title={title}
      extra={extra}
      bordered={false}
      style={styles.card}
      headStyle={styles.cardHead}
      bodyStyle={{ ...bodyStyle }}
    >
      {children}
    </Card>
  );

  const renderStatCard = ({ label, value, hint }) => (
    <Card bordered={false} style={styles.statCard}>
      <div style={styles.statCircle} />

      <div style={styles.statContent}>
        <div style={styles.statLabel}>{label}</div>
        <div style={styles.statValue}>{value}</div>
        {hint && <div style={styles.statHint}>{hint}</div>}
      </div>
    </Card>
  );

  const renderDonutChart = (items = []) => {
    if (!items.length) return <Empty />;

    const normalizedItems = items.map((item, index) => ({
      ...item,
      color: CHART_COLORS[index % CHART_COLORS.length],
      value: Number(item.value) || 0,
    }));

    const rawTotal = normalizedItems.reduce((sum, item) => sum + item.value, 0);
    const visibleItems =
      rawTotal > 0
        ? normalizedItems.filter((item) => item.value > 0)
        : normalizedItems;

    if (!visibleItems.length) return <Empty />;

    const chartTotal = rawTotal || visibleItems.length;
    const center = 110;
    const radius = 72;
    const strokeWidth = 42;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    return (
      <div className="app-w-full">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px 16px",
            marginBottom: 8,
            fontSize: 12,
            color: "var(--color-heading)",
          }}
        >
          {visibleItems.map((item) => (
            <span
              key={item.label}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <i
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 1,
                  display: "inline-block",
                  backgroundColor: item.color,
                }}
              />
              {item.label}
            </span>
          ))}
        </div>

        <svg
          viewBox="0 0 220 220"
          role="img"
          style={{
            width: "min(260px, 100%)",
            height: 260,
            display: "block",
            margin: "0 auto",
          }}
        >
          {visibleItems.map((item) => {
            const percent = rawTotal ? item.value / chartTotal : 1 / chartTotal;
            const dash = percent * circumference;
            const currentOffset = offset;
            const middlePercent = currentOffset / circumference + percent / 2;
            offset += dash;

            const labelX =
              center + Math.cos((middlePercent - 0.25) * 2 * Math.PI) * radius;
            const labelY =
              center + Math.sin((middlePercent - 0.25) * 2 * Math.PI) * radius;

            return (
              <g key={item.label}>
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={item.color}
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={-currentOffset}
                  transform={`rotate(-90 ${center} ${center})`}
                />
                <text
                  x={labelX}
                  y={labelY + 4}
                  fill="#fff"
                  fontSize="13"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {formatDonutValue(item.value)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  const renderSalesTrendChart = () => {
    const salesData = dashboardData.last_15_days_sale?.length
      ? dashboardData.last_15_days_sale
      : [{ date: "-", sales: 0 }];

    const width = 780;
    const height = 285;
    const padding = { top: 12, right: 18, bottom: 38, left: 38 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const maxSales = Math.max(
      ...salesData.map((item) => Number(item.sales) || 0),
    );
    const axisMax = Math.max(10, Math.ceil(maxSales / 10) * 10 || 100);
    const step = axisMax / 10;
    const ticks = Array.from({ length: 10 }, (_, index) => step * (index + 1));

    const points = salesData.map((item, index) => {
      const x =
        padding.left +
        (salesData.length === 1
          ? plotWidth / 2
          : (index / (salesData.length - 1)) * plotWidth);
      const y =
        padding.top +
        plotHeight -
        ((Number(item.sales) || 0) / axisMax) * plotHeight;

      return {
        x,
        y,
        label: item.date,
        value: Number(item.sales) || 0,
      };
    });

    const clampY = (value) =>
      Math.min(padding.top + plotHeight, Math.max(padding.top, value));

    const getCurvePath = (chartPoints) => {
      if (chartPoints.length === 1) {
        return `M ${chartPoints[0].x} ${chartPoints[0].y}`;
      }

      return chartPoints.reduce((path, point, index) => {
        if (index === 0) return `M ${point.x} ${point.y}`;

        const previousPoint = chartPoints[index - 1];
        const beforePreviousPoint = chartPoints[index - 2] || previousPoint;
        const nextPoint = chartPoints[index + 1] || point;

        const controlPointOneX =
          previousPoint.x + (point.x - beforePreviousPoint.x) / 6;
        const controlPointOneY = clampY(
          previousPoint.y + (point.y - beforePreviousPoint.y) / 6,
        );
        const controlPointTwoX = point.x - (nextPoint.x - previousPoint.x) / 6;
        const controlPointTwoY = clampY(
          point.y - (nextPoint.y - previousPoint.y) / 6,
        );

        return `${path} C ${controlPointOneX} ${controlPointOneY}, ${controlPointTwoX} ${controlPointTwoY}, ${point.x} ${point.y}`;
      }, "");
    };

    const linePath = getCurvePath(points);
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];
    const baseY = padding.top + plotHeight;
    const areaPath = `${linePath} L ${lastPoint.x} ${baseY} L ${firstPoint.x} ${baseY} Z`;
    const labelStep =
      salesData.length > 8 ? Math.ceil(salesData.length / 7) : 1;

    return (
      <div
        dir="ltr"
        style={{
          width: "100%",
          height: 310,
          paddingBottom: 4,
          overflow: "hidden",
        }}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={t("sales_overview")}
          preserveAspectRatio="none"
          style={{ width: "100%", height: "100%", display: "block" }}
        >
          <defs>
            <linearGradient
              id="homeSalesAreaGradient"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor="#e7ff9f" stopOpacity="0.96" />
              <stop offset="58%" stopColor="#efffc4" stopOpacity="0.72" />
              <stop offset="100%" stopColor="#f5ffd9" stopOpacity="0.58" />
            </linearGradient>

            <clipPath id="homeSalesPlotClip">
              <rect
                x={padding.left}
                y={padding.top}
                width={plotWidth}
                height={plotHeight}
              />
            </clipPath>
          </defs>

          {ticks.map((tick) => {
            const y = padding.top + plotHeight - (tick / axisMax) * plotHeight;

            return (
              <g key={tick}>
                <line
                  x1={padding.left}
                  x2={width - padding.right}
                  y1={y}
                  y2={y}
                  stroke="#e9edf5"
                  strokeWidth="1"
                />
                <text
                  x={padding.left - 12}
                  y={y + 4}
                  fill="#666b75"
                  fontSize="12"
                  fontWeight="600"
                  textAnchor="end"
                >
                  {Math.round(tick)}
                </text>
              </g>
            );
          })}

          <line
            x1={padding.left}
            x2={padding.left}
            y1={padding.top}
            y2={baseY}
            stroke="#e4eaf2"
            strokeWidth="1"
          />

          <g clipPath="url(#homeSalesPlotClip)">
            <path d={areaPath} fill="url(#homeSalesAreaGradient)" />
            <path
              d={linePath}
              fill="none"
              stroke="#0d123b"
              strokeWidth="3.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>

          {points.map((point, index) => (
            <circle
              key={`${point.label}-${index}`}
              cx={point.x}
              cy={point.y}
              r="4"
              fill="#e7f6b7"
              stroke="#0d123b"
              strokeWidth="1.6"
            >
              <title>{`${point.label}: ${roundOff(point.value)}`}</title>
            </circle>
          ))}

          {points.map((point, index) => {
            const showLabel =
              index === 0 ||
              index === points.length - 1 ||
              index % labelStep === 0;

            return showLabel ? (
              <text
                key={`label-${point.label}-${index}`}
                x={point.x}
                y={height - 12}
                fill="#666b75"
                fontSize="12"
                fontWeight="600"
                textAnchor="middle"
              >
                {point.label}
              </text>
            ) : null;
          })}
        </svg>
      </div>
    );
  };

  const renderSalesInsight = () => {
    if (!dashboardData.branch_selling_products.length) return <Empty />;

    return (
      <div style={styles.list}>
        {dashboardData.branch_selling_products.slice(0, 4).map((product, i) => (
          <div style={styles.softRow} key={product.id || i}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 14,
                  background: "var(--color-white)",
                  border: "1px solid var(--color-border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--color-heading)",
                  fontWeight: 800,
                  overflow: "hidden",
                  flex: "0 0 auto",
                }}
              >
                {product.image ? (
                  <img
                    src={product.image}
                    alt={getProductName(product)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  getProductName(product).charAt(0).toUpperCase()
                )}
              </div>

              <div style={{ minWidth: 0 }}>
                <h4
                  style={{
                    margin: 0,
                    color: "var(--color-heading)",
                    fontSize: 14,
                    fontWeight: 800,
                    maxWidth: 260,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getProductName(product)}
                </h4>
                <p
                  style={{
                    margin: "4px 0 0",
                    color: "var(--color-text)",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {product.total_sold || 0} {t("sold")}
                </p>
              </div>
            </div>

            <strong
              style={{
                color: "var(--color-heading)",
                fontSize: 15,
                fontWeight: 800,
                whiteSpace: "nowrap",
              }}
            >
              {product.total_sold || 0}
            </strong>
          </div>
        ))}
      </div>
    );
  };

  const renderCategoryList = () => {
    if (!categoryItems.length) return <Empty />;

    return (
      <div style={styles.list}>
        {categoryItems.map((item, index) => (
          <div
            key={`${item.name}-${index}`}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                color: "var(--color-text)",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <span>{item.name}</span>
              <b style={{ color: "var(--color-heading)" }}>{item.percent}%</b>
            </div>

            <div
              style={{
                height: 10,
                borderRadius: 999,
                background: "var(--color-bg)",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  display: "block",
                  height: "100%",
                  width: `${item.percent}%`,
                  borderRadius: "inherit",
                  background:
                    "linear-gradient(90deg, var(--color-heading), var(--color-primary))",
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStockList = (items = [], isLowStock = false) => {
    if (!is_stock_management_active) {
      return <Tag color="orange">{t("disabled")}</Tag>;
    }

    if (!items?.length) return <Empty />;

    return (
      <div style={styles.list}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            padding: "0 0 10px",
            borderBottom: "1px solid var(--color-border)",
            color: "var(--color-text)",
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          <span>{t("product")}</span>
          <span>{t("stock")}</span>
        </div>

        {items.slice(0, 6).map((product, i) => (
          <div
            key={product.id || i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              padding: "10px 0",
              borderBottom: "1px solid var(--color-border)",
              color: "var(--color-heading)",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <span>
              {appConfig.direction === "ltr" ? product.name_en : product.name}
            </span>
            <span
              style={{
                color: isLowStock
                  ? "var(--color-danger)"
                  : "var(--color-heading)",
                fontWeight: isLowStock ? 800 : 600,
              }}
            >
              {product.stocks}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const loggedInUserName = authUser.name || "-";

  const loggedInBranchName = authUser.branch_name || "";

  return (
    <>
      <Spin spinning={isLoading}>
        <div style={styles.page} dir={appConfig.direction}>
          <Row gutter={[20, 20]} align="stretch">
            <Col xs={24} xl={12}>
              <div style={styles.hero}>
                <div style={styles.heroGlow} />

                <div style={styles.heroContent}>
                  <div style={styles.heroEyebrow}>{t("hi")}</div>

                  <h1 style={styles.heroTitle}>{loggedInUserName}</h1>

                  {loggedInBranchName && (
                    <div style={styles.heroMeta}>
                      <span style={styles.heroBranchPill}>
                        <span style={styles.heroBranchDot} />
                        {`${loggedInBranchName} ${t("branch")}`}
                      </span>
                    </div>
                  )}
                </div>

                <div style={styles.heroMark}>⚡</div>
              </div>
            </Col>

            <Col xs={24} sm={12} xl={6}>
              {renderStatCard({
                label: t("total_sales"),
                value: renderMoneyValue(dashboardData.totals?.amount),
              })}
            </Col>

            <Col xs={24} sm={12} xl={6}>
              {renderStatCard({
                label: t("total_taxes_on_sale"),
                value: renderMoneyValue(dashboardData.totals?.tax),
                hint: "VAT",
              })}
            </Col>
          </Row>

          <Row gutter={[20, 20]}>
            <Col xs={24} md={8}>
              {renderStatCard({
                label: t("refunds"),
                value: renderMoneyValue(dashboardData.totals?.refunds),
              })}
            </Col>

            <Col xs={24} md={8}>
              {renderStatCard({
                label: t("completed_orders"),
                value: is_order_management_active ? (
                  dashboardData.total_orders || 0
                ) : (
                  <Tag color="orange">{t("disabled")}</Tag>
                ),
              })}
            </Col>

            <Col xs={24} md={8}>
              {renderStatCard({
                label: t("pending_orders"),
                value: is_order_management_active ? (
                  dashboardData.waiting_for_approval || 0
                ) : (
                  <Tag color="orange">{t("disabled")}</Tag>
                ),
              })}
            </Col>
          </Row>

          <Row gutter={[20, 20]}>
            <Col xs={24} lg={12}>
              {renderDashboardCard({
                title: t("sales_overview"),
                bodyStyle: { minHeight: 350 },
                children: renderSalesTrendChart(),
              })}
            </Col>

            <Col xs={24} lg={12}>
              {renderDashboardCard({
                title: t("sales_insight"),
                bodyStyle: { minHeight: 350 },
                children: renderSalesInsight(),
              })}
            </Col>
          </Row>

          <Row gutter={[20, 20]}>
            <Col xs={24} md={12} xl={8}>
              {renderDashboardCard({
                title: t("quarterly_sales"),
                bodyStyle: {
                  minHeight: 316,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                children: renderDonutChart(quarterlySalesData),
              })}
            </Col>

            <Col xs={24} md={12} xl={8}>
              {renderDashboardCard({
                title: t("payment_types"),
                bodyStyle: {
                  minHeight: 316,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                children: renderDonutChart(paymentTypesData),
              })}
            </Col>

            <Col xs={24} md={12} xl={8}>
              {renderDashboardCard({
                title: t("quarterly_tax"),
                bodyStyle: {
                  minHeight: 316,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                children: renderDonutChart(quarterlyTaxData),
              })}
            </Col>
          </Row>

          <Row gutter={[20, 20]}>
            <Col xs={24} md={12} xl={8}>
              {renderDashboardCard({
                title: t("products_by_category"),
                children: renderCategoryList(),
              })}
            </Col>

            <Col xs={24} md={12} xl={8}>
              {renderDashboardCard({
                title: t("total_stock"),
                extra:
                  dashboardData.inventry_status?.length > 0 ? (
                    <Button
                      type="primary"
                      size="small"
                      htmlType="button"
                      onClick={() => {
                        navigate("/inventory/stock");
                      }}
                    >
                      {t("view_all")}
                    </Button>
                  ) : null,
                children: renderStockList(dashboardData.inventry_status),
              })}
            </Col>

            <Col xs={24} md={12} xl={8}>
              {renderDashboardCard({
                title: t("low_stock"),
                extra:
                  dashboardData.low_stock_products?.length > 0 ? (
                    <Button
                      type="primary"
                      size="small"
                      htmlType="button"
                      onClick={() => {
                        navigate("/inventory/low-stock");
                      }}
                    >
                      {t("view_all")}
                    </Button>
                  ) : null,
                children: renderStockList(
                  dashboardData.low_stock_products,
                  true,
                ),
              })}
            </Col>
          </Row>
        </div>
      </Spin>

      <TrialAddonsModal
        open={trialModalVisible}
        expiringAddons={expiringAddons}
        onClose={() => setTrialModalVisible(false)}
        setGlobalLoading={setIsLoading}
      />
    </>
  );
};

export default Home;
